'use client';
import { useEffect, useMemo, useState } from 'react';
import { useObserverElementById } from '../../hooks/use-observer-element-by-id';
import { NavigationItemData } from './navigation';

const DEFAULT_OFFSET_TOP = 300;

export const useHandleScrollToSection = (options?: {
  offsetTop?: number;
  scrollOptions?: Omit<ScrollToOptions, 'top'>;
}) => {
  return (id: string) => {
    const el = document.querySelector(`#${id}`);
    const boundingRect = el?.getBoundingClientRect();

    if (!boundingRect) return;
    const scrollOffset =
      boundingRect.top +
      window.scrollY -
      (options?.offsetTop ?? DEFAULT_OFFSET_TOP);

    window.scrollTo({
      top: scrollOffset,
      behavior: 'smooth',
      ...options?.scrollOptions,
    });
  };
};

export const useNavigation = (
  menuItems: NavigationItemData[],
  options?: {
    offsetTop?: number;
    scrollOptions?: Omit<ScrollToOptions, 'top'>;
  }
) => {
  const [lockedActiveId, setLockedActiveId] = useState<string | undefined>(
    undefined
  );
  const ids = useMemo(() => menuItems.map((item) => item.id), [menuItems]);
  const activeId = useObserverElementById({
    ids: ids,
  });

  useEffect(() => {
    const clearLock = () => {
      setLockedActiveId(undefined);
    };
    window.addEventListener('wheel', clearLock);
    window.addEventListener('touchmove', clearLock);
    return () => {
      window.removeEventListener('wheel', clearLock);
      window.removeEventListener('touchmove', clearLock);
    };
  }, [activeId]);

  const handleScrollToItem = useHandleScrollToSection(options);

  const handleItemClick = (item: NavigationItemData) => {
    setLockedActiveId(item.id);
    handleScrollToItem(item.id);
  };

  return {
    activeId,
    handleItemClick,
    lockedActiveId,
  };
};
